import React, { useState, useEffect, useMemo, Suspense } from 'react'
import {
    Grid,
    makeStyles,
    useTheme,
    useMediaQuery,
    alpha,
} from '@material-ui/core';
import { XXLGrid } from '../../common/XXLGrid'
import ComponentWrapper from '../../common/ComponentWrapper';
import { Fallback } from '../../common/LayoutComponents';
import { useLayout } from '../../../state/layoutState';
import { useCalculator } from '../../../state/calculatorState';
import {
    payOptions,
    TAXABLE_INCOME,
    SUPERANNUATION,
    TAX,
    NO_YTD,

    YEAR,
    MONTH,
    FORTNIGHT,
    WEEK,
    CALCULATION_OBJECT,
    NETT_PAY,
} from '../../../core/calculatorConstants';
import { getTaxYearIndex } from '../../../core/calculatorUtils';
import { taxData } from '../../../core/calculatorData.js';
import { ID_SUMMARY } from '../../../constants';
import { formatMoney } from '../../../utils/utils';
import { PC_CONTENT_1 } from '../../ads/FuseAds';

import { GetSummaryNotes } from './SummaryNotes'

// import { SummaryChart } from './SummaryChart';
// import { SummaryTaxBand } from './SummaryTaxBand';
// import { SuperannuationBand } from './SummarySuperannuationBand';
// import { SummaryPercentile } from './SummaryPercentile';
// import { SummaryTable } from './SummaryTable';
// import { SummaryOptions } from './SummaryOptions';


const SummaryChart = React.lazy(() => import('./SummaryChart'));
const SummaryTaxBand = React.lazy(() => import('./SummaryTaxBand'));
const SuperannuationBand = React.lazy(() => import('./SummarySuperannuationBand'));
const SummaryPercentileBand = React.lazy(() => import('./SummaryPercentileBand'));
const SummaryTable = React.lazy(() => import('./SummaryTable'));
const SummaryOptions = React.lazy(() => import('./SummaryOptions'));


const useStyles = makeStyles(theme => {
    return {
        root: {
            '& > *': {
                margin: theme.spacing(1),
            },
        },

        contentWrapper: {
            padding: 10,
        }
    }
});


export const SummaryContext = React.createContext({});


export const SummaryComponent = React.forwardRef((props, ref) => {
    const classes = useStyles();
    const theme = useTheme();
    const small = useMediaQuery(theme.breakpoints.down('sm'));
    const xs = useMediaQuery(theme.breakpoints.only('xs'));
    const sm = useMediaQuery(theme.breakpoints.only('sm'));
    const md = useMediaQuery(theme.breakpoints.only('md'));
    const lg = useMediaQuery(theme.breakpoints.only('lg'));
    const xl = useMediaQuery(theme.breakpoints.only('xl'));

    const breakpointKey = xs ? "xs" : sm ? "sm" : md ? "md" : lg ? "lg" : "xl";


    const [{ resultsRange, printing },] = useLayout();
    const [calculator ] = useCalculator();
    const range = payOptions[resultsRange].range;

    // perform a rerender on this component to ensure the fonts are rendered properly
    const [, setDelayed] = useState(true);
    useEffect(() => {
        const timeout = setTimeout(() => setDelayed(false), 1000);
        return () => clearTimeout(timeout);
    }, []);


    const summaryData = computeSummaryData(calculator, theme, range, calculator.ytd);


    const paddingStyle = { paddingTop: 10, paddingBottom: xl ? 10 : 0, paddingLeft: small ? 10 : 10, paddingRight: small ? 10 : 10 };
    return (
        <div id={ID_SUMMARY} ref={ref}>
            <SummaryContext.Provider value={summaryData}>
                <ComponentWrapper title="Summary">
                    <div className={classes.contentWrapper} style={paddingStyle} >
                        <XXLGrid container >
                            <XXLGrid item xs={12}>
                                <Suspense fallback={<Fallback />}>
                                    <SummaryOptions />
                                </Suspense>
                            </XXLGrid>
                            <XXLGrid item xs={12} xxl={8}>
                                <Suspense fallback={<Fallback />}>
                                    <SummaryTable />
                                </Suspense>
                                {!xl && <PC_CONTENT_1 key={`PC_CONTENT_1_${breakpointKey}`} />}

                            </XXLGrid>
                            {!printing && (
                                <XXLGrid item xs={12} xxl={4}>
                                    <XXLGrid container spacing={0} style={{ padding: 0 }}>
                                        <XXLGrid item xs={12} sm={12} md={6} xxl={12}>
                                            <Suspense fallback={<Fallback />}>

                                                <SummaryChart />

                                            </Suspense>
                                        </XXLGrid>
                                        <XXLGrid item xs={12} sm={12} md={6} xxl={12} >
                                            <Suspense fallback={<Fallback />}>
                                                <SummaryTaxBand />
                                            </Suspense>
                                            <Suspense fallback={<Fallback />}>
                                                <SuperannuationBand />
                                            </Suspense>
                                            <Suspense fallback={<Fallback />}>
                                                <SummaryPercentileBand />
                                            </Suspense>
                                        </XXLGrid>
                                    </XXLGrid>
                                </XXLGrid>
                            )}
                        </XXLGrid>
                    </div>
                </ComponentWrapper>
            </SummaryContext.Provider>
        </div>
    )
})



const computeSummaryData = (calculator, theme, range, ytd) => {
    let {
        year,

        payOption,
        includeUnpaidLeave,
        unpaidPublicHolidays,
        unpaidLeave,

        fulltime,
        casual,

        nonResident,
        backpacker,
        noTaxFree,

        net,
        netIncome,
        income,
        baseSalary,
        overtimeSalary,
        adjustDeductions,

        superannuationSacrafice,
        adjustSalaryScaracfice,
        incomeTax,
        extraWithholdingTax,
        grossTax,
        medicare,
        medicareSurchargeLiability,
        medicareAdjustment,
        applyMedicareAdjustment,
        medicareDescription,
        HELP, help,
        otherTaxesAndLevies,
        division293,
        additionalSuper,
        totalSuperannuation,
        superannuation,
        adjustSuperannuation,

        allowance,
        adjustAllowanceIncome,
        allowanceIncomeTaxable,
        capitalGains,
        otherIncome,
        adjustOtherIncome,
        salarySacraficeIncome,
        novatedLease,

        businessIncome,
        includeGST,
        businessIncomeTax,
        businessIncomeLoss,
        businessIncomeLossTax,

        hasBonus,
        bonusIncome,
        bonusIncomeObj,
        bonusIncomeTax,

        spouseSuperTaxOffset,
        superannuationCoContribution,

        superannuationReportable,
        superannuationUntaxedNonConcessional,
        superannuationExcessTax,
        superannuationExcessConcessionalContributionsOffset,
        concessionalAdditionalSuper,
        superannuationConcessionalSavings,

        superannuationConcessional,
        superannationNonConcessional,
        superannuationExcess,

        fringeBenefitsAnnual,

        taxableDeductions,
        taxableDeductionsTax,
        capitalGainsTax,
        otherIncomeTax,
        taxCredits,
        frankingCredits,
        ftb,

        offsets,
        listo,
        lito,
        lamito,
        sapto,
        superannuationTax,
        payments,
        YTD,
        warnings,
    } = calculator;


    // colors
    const primary = theme.palette.primary;
    const secondary = theme.palette.secondary;
    const tertiary = theme.palette.tertiary;
    const nc = alpha(theme.palette.background.overlay, 0.5);
    const clear = "#00000000";

    let disableHELP = (nonResident && backpacker)

    let businessIncomeDeductable = includeGST ? businessIncome / 1.1 : businessIncome
    let businessIncomeGST = businessIncome - businessIncomeDeductable;


    // only include unpaid if not fulltime and casual rate is not using annual days worked
    let unpaid = fulltime ? 0 : (casual.frequency === YEAR) ? 0 : (includeUnpaidLeave) ? Number(unpaidLeave) + Number(unpaidPublicHolidays) : 0;

    let taxReturn_withheld = 0;//(incomeTax.m + medicare.m) * 12 - (incomeTax.a + medicare.a);
    let studentloan_withheld = 0

    const yearIndex = getTaxYearIndex(year);
    const helpData = taxData[yearIndex].help
    const hasWithholdingHeldpData = helpData.bracketsWithholding
    let studentLoanTitle = hasWithholdingHeldpData ? "Student Loan¹" : "Student Loan"

    let taxReturn = 0;

    let otherIncomeSources = capitalGainsTax !== 0 ||
        otherIncomeTax !== 0 ||
        bonusIncomeTax !== 0 ||
        businessIncomeTax !== 0 ||
        businessIncomeLossTax !== 0

    let hideTaxReturn = otherIncomeSources || !fulltime || payOption === 4 || payOption === 5 // HIDE THE TAX RETURN IF  not fulltime or daily or HOURLY PAY OPTION IS SET


    switch (payOption) {
        case 0:
        case 1:
            // monthly
            taxReturn = (grossTax.m * 12) - grossTax.a;
            taxReturn_withheld = (incomeTax.m + medicare.m) * 12 - (incomeTax.a + medicare.a);
            studentloan_withheld = (help.m * 12) - (help.a);
            break;
        case 2:
            // fortnightly
            taxReturn = (grossTax.f * payments.f - grossTax.a);
            taxReturn_withheld = (incomeTax.f + medicare.f) * payments.f - (incomeTax.a + medicare.a);
            studentloan_withheld = (help.f * payments.f) - (help.a);
            break;
        case 3:
            // weekly
            taxReturn = (grossTax.w * payments.w) - grossTax.a;
            taxReturn_withheld = (incomeTax.w + medicare.w) * payments.w - (incomeTax.a + medicare.a);
            studentloan_withheld = (help.w * payments.w) - (help.a);
            break;
        default:
            // hourly or daily
            taxReturn = (grossTax.d * payments.d) - grossTax.a;
            taxReturn_withheld = (incomeTax.d + medicare.d) * payments.d - (incomeTax.a + medicare.a);
            studentloan_withheld = (help.d * payments.d) - (help.a);

             break
    }

    ///deal with casual incomes
    if (!fulltime) {

        switch (payOption) {
            case 4: // weekly
            case 5: // daily
                switch (casual.frequency) {
                    case WEEK: //FREQUENCY[0]: //"Week"
                        // casual.annual = weeks per year
                        taxReturn = (grossTax.w * casual.annual) - grossTax.a - grossTax.d*unpaid
                        taxReturn_withheld = (incomeTax.w + medicare.w) * (casual.annual) - (incomeTax.a + medicare.a) - (incomeTax.d + medicare.d)*unpaid
                        studentloan_withheld = (help.w * casual.annual) - (help.a) - (help.d)*unpaid
                        break;
                    case FORTNIGHT: //FREQUENCY[1]: //"Fortnight"
                        // casual.annual = fortnights per year
                        taxReturn = (grossTax.f * casual.annual) - grossTax.a - grossTax.d*unpaid
                        taxReturn_withheld = (incomeTax.f + medicare.f) * casual.annual - (incomeTax.a + medicare.a) - (incomeTax.d + medicare.d)*unpaid
                        studentloan_withheld = (help.f * casual.annual) - (help.a) - (help.d)*unpaid
                        break;
                    case MONTH: //FREQUENCY[2]: //"Monthly"
                        // casual.annual = months per year
                        taxReturn = (grossTax.m * casual.annual) - grossTax.a - grossTax.d*unpaid
                        taxReturn_withheld = (incomeTax.m + medicare.m) * casual.annual - (incomeTax.a + medicare.a) - (incomeTax.d + medicare.d)*unpaid
                        studentloan_withheld = (help.m) * casual.annual - (help.a) - (help.d)*unpaid
                        break;
                    case YEAR: //FREQUENCY[3]: //"Annually"
                        // casual.days = days per year
                        let ratio = casual.days / 5; // fraction of weeks per year
                        taxReturn = (grossTax.w * ratio) - grossTax.a - grossTax.d*unpaid
                        taxReturn_withheld = (incomeTax.m + medicare.m) * ratio - (incomeTax.a + medicare.a) - (incomeTax.d + medicare.d)*unpaid
                        studentloan_withheld = (help.m * ratio) - (help.a) - (help.d)*unpaid
                        break;
                    default:
                        break;
                }
                break;
            default:
                break;
        }

    }

    taxReturn_withheld -= (superannuationConcessionalSavings + taxableDeductionsTax + capitalGainsTax);
    taxReturn_withheld -= otherIncomeTax; // deduct other tax liabilitiies
    taxReturn_withheld -= bonusIncomeTax; // deduct other tax liabilitiies
    taxReturn_withheld -= businessIncomeTax; // deduct business tax liabilitiies

    taxReturn_withheld -= businessIncomeLossTax; // deduct business loss tax liabilitiies


    // nettBonus Income:
    let bonusIncomeNett = {...CALCULATION_OBJECT}
    if( hasBonus) {
        let taxfactor = 1 + bonusIncomeTax/bonusIncomeObj.a;
        bonusIncomeNett.a = bonusIncomeObj.a * taxfactor
        bonusIncomeNett.m = bonusIncomeObj.m * taxfactor
        bonusIncomeNett.f = bonusIncomeObj.f * taxfactor
        bonusIncomeNett.w = bonusIncomeObj.w * taxfactor
        bonusIncomeNett.d = bonusIncomeObj.d * taxfactor
    }


    const superTaxComment = listo && range === "a" ? `Tax taken from your super fund. Includes offset.` : "Tax taken from your super fund";

    const offsetsComment = [];
    if (lamito > 0) offsetsComment.push("LMITO");
    if (lito > 0) offsetsComment.push("LITO");
    if (sapto > 0) offsetsComment.push("SAPTO");
    if (spouseSuperTaxOffset > 0) offsetsComment.push("Spouse super")



    const annual = range === 'a';
    const monthly = range === 'm';

    // const isYTD =  !casual && ytd;
    const isYTD = ytd;

    // simulate super classes
    const superGuarantee = superannuation;// spreadAnnualAmounts({ a: superannuationGuarantee });
    // const includedSuper = combine(superannuation, superannuationReportable);

    const superGuaranteeComment = warnings.superannuationCapped ? "Capped to Maximum Contributions Base" : '';

    const totalReportableSuper = combine(superannuationSacrafice, superannuationReportable)

    const personalContributions = adjustSuperannuation ? additionalSuper : 0;// - superannuationCoContributionNonConcessional;

    // Summary data
    // const payAsterisks =  (adjustSuperannuation && personalContributions > 0 ) || ( adjustSuperannuationCarryForward && superannuationCarryForward > 0 ) ? "*" : "";
    // const payAsterisks =  (adjustSuperannuation && personalContributions > 0 ) ? "*" : "";

    const payAsterisks = (taxableDeductions !== 0 || allowance.a !== 0 || capitalGains !== 0 || otherIncome !== 0 || adjustSuperannuation || businessIncome !== 0) ? "*" : "";

    const summaryData = {};
    summaryData.summary = {
        label: "Pay",
        comment: payAsterisks,
        data: createData(net, range, YTD), color: primary.main, colorB: primary.light,
        sub: []
    }

    if (personalContributions > 0 || taxableDeductions > 0) {
        let label = "Annual income after "
        let empty = true
        if (personalContributions > 0) {
            label += "voluntary Super"
            empty = false
        }
        if (taxableDeductions) {
            if (!empty) {
                label += " and"
            }
            label += " deductions"
        }

        summaryData.net = {
            label: label,
            data: netIncome,
            color: primary.light,
            colorB: primary.light,
        }
    }



    if (ftb.a > 0) {
        summaryData.summary.sub.push(
            {
                label: "(With Family Tax Benefit)",
                data: createData(combine(ftb, net), range, NO_YTD)
            }
        )
    }


    if ( fringeBenefitsAnnual.a > 0) {
        summaryData.summary.sub.push(
            {
                label: "(With Fringe Benefits)",
                data: createData(combineExcludeZero(fringeBenefitsAnnual, net), range, NO_YTD)
            }
        )
    }

    if (hasBonus) {
        summaryData.summary.sub.push(
            {
                label: "Includes Bonus",
                data: createData((bonusIncomeNett), range, NO_YTD)
            }
        )
    }





    if (hideTaxReturn) {
        summaryData.return = undefined
    } else {
        summaryData.return = {
            label: "Estimated Tax Return", id: "expandReturn", comment: "", data: createData(taxReturn, range, NO_YTD), color: alpha(theme.palette.background.overlay, 0.25),
            sub: [],
        }


        if (taxReturn_withheld > 0) {
            summaryData.return.sub.push(
                { label: "Tax withheld credits", comment: "Excess income tax withheld", data: createData(taxReturn_withheld, range, NO_YTD), color: primary[300] },
            )
        }
        if (studentloan_withheld !== 0) {
            summaryData.return.sub.push(
                { label: "Student Loan withheld credits", comment: "Excess Student loan payments", data: createData(studentloan_withheld, range, NO_YTD), color: primary[300] },
            )
        }

        // taxableDeductionsTax,
        // capitalGainsTax,
        // otherIncomeTax,

        if (taxableDeductionsTax !== 0) {
            summaryData.return.sub.push(
                { label: "Taxable deductions benefit", comment: "", data: createData(taxableDeductionsTax, range, NO_YTD), color: primary[300] },
            )
        }

        if (capitalGainsTax < 0) {
            summaryData.return.sub.push(
                { label: "Capital gains liability", comment: "", data: createData(capitalGainsTax, range, NO_YTD), color: primary[300] },
            )
        }
        if (capitalGainsTax > 0) {
            summaryData.return.sub.push(
                { label: "Capital gains benefit (HELP)", comment: "", data: createData(capitalGainsTax, range, NO_YTD), color: primary[300] },
            )
        }


        if (otherIncomeTax < 0) {
            summaryData.return.sub.push(
                { label: "Other income liability", comment: "", data: createData(otherIncomeTax, range, NO_YTD), color: primary[300] },
            )
        }

        if (otherIncomeTax > 0) {
            summaryData.return.sub.push(
                { label: "Income loss tax reduction", comment: "", data: createData(otherIncomeTax, range, NO_YTD), color: primary[300] },
            )
        }

        if (bonusIncomeTax !== 0) {
            summaryData.return.sub.push(
                { label: "Bonus pay tax liability", comment: "", data: createData(bonusIncomeTax, range, NO_YTD), color: primary[300] },
            )
        }


        if (businessIncomeTax !== 0) {
            summaryData.return.sub.push(
                { label: "Business income tax liability", comment: "", data: createData(businessIncomeTax, range, NO_YTD), color: primary[300] },
            )
        }

        if (businessIncomeLossTax !== 0) {
            summaryData.return.sub.push(
                { label: "Business loss tax reduction", comment: "", data: createData(businessIncomeLossTax, range, NO_YTD), color: primary[300] },
            )
        }


        if (offsets.a !== 0) {
            summaryData.return.sub.push(
                { label: "Tax offsets", comment: offsetsComment.join(", "), data: createData(offsets.a, range, NO_YTD), color: primary[300] },
            )
        }


        if (medicareSurchargeLiability !== 0) {
            summaryData.return.sub.push(
                { label: "Medicare Surcharge", comment: "", data: createData(-1 * medicareSurchargeLiability, range, NO_YTD), color: primary[300] },
            )
        }


        if (superannuationConcessionalSavings !== 0) {
            summaryData.return.sub.push(
                { label: "Voluntary Superannuation tax reduction", comment: "", data: createData(superannuationConcessionalSavings, range, NO_YTD), color: primary[300] },
            )
        }

        if (division293 !== 0) {
            summaryData.return.sub.push(
                { label: "Division 293 liability", comment: "", data: createData(-1 * division293, range, NO_YTD), color: primary[300] },
            )
        }

        if (taxReturn_withheld < 0) {
            // special case where tax withheld is 0 and only have a medicare debt
            if (Math.floor(Math.abs(taxReturn_withheld)) === Math.floor(Math.abs(medicare.a))) {
                summaryData.return.sub.push(
                    { label: "Medicare debt", comment: "Medicare liability", data: createData(taxReturn_withheld, range, NO_YTD), color: primary[300] },
                )
            } else {
                summaryData.return.sub.push(
                    { label: "Tax withheld debt", comment: "", data: createData(taxReturn_withheld, range, NO_YTD), color: primary[300] },
                )
            }
        }
    }



    summaryData.notes = {
        label: "Summary Notes", id: "expandNotes", color: alpha(theme.palette.background.overlay, 0.25), sub: GetSummaryNotes()
    }


    summaryData.extraPayment = annual || monthly ? false : warnings.extraPayment;
    summaryData.payments = annual ? 1 : payments[range];
    summaryData.ytd = isYTD;
    summaryData.YTD = YTD[range];

    let netTaxableIncome = annualAmount(net) + annualAmount(grossTax);
    let taxPercent = annualAmount(grossTax) / netTaxableIncome;
    taxPercent = Math.round(taxPercent * 1000) / 10;

    // tax categoy
    let taxCategoryComment = "";
    if (nonResident) taxCategoryComment = "Non-resident";
    if (backpacker) taxCategoryComment = "Working Holiday Maker";
    if (noTaxFree) taxCategoryComment = "No tax-free Threshold";

    summaryData.data = [

        // PAY
        {
            label: NETT_PAY, shortLabel: "Pay", comment: "", id: "expandPay", data: createData(net, range, YTD), color: primary.main, hidden: true,
            chart: {
                label: "Pay", value: formatValue(net.a), comment:(hasBonus ? `including ${formatValue(bonusIncomeNett.a)} bonus` : "")
            },
            sub: [
                { label: "Take home pay", comment: "", data: createData(net, range, YTD), color: primary[300] },
                { label: "Bonus", comment: "", data: createData(bonusIncomeNett, range, YTD), color: primary[400], hidden:!hasBonus },
                { label: "Family Tax Benefit", comment: "", data: createData(ftb, range, YTD), squareBrackets:true, color: primary[500], hidden:ftb.a <= 0 },
                { label: "Fringe Benefits", comment: "", data: createData(fringeBenefitsAnnual, range, YTD), squareBrackets:true, color: primary[500], hidden:fringeBenefitsAnnual.a <= 0 },
            ]
        },


        // INCOME
        {
            label: TAXABLE_INCOME, shortLabel: "Income", comment: "", id: "expandIncome", data: createData(income, range, YTD), color: primary.main,
            chart: {
                label: "Income", value: formatValue(income.a)
            },
            sub: [
                { label: "Base salary", comment: "", data: createData(baseSalary, range, YTD), color: primary[300] },
                { label: "Salary sacrifice", comment: "", data: createData(salarySacraficeIncome, range, YTD), negative: true, hidden: (salarySacraficeIncome.a <= 0), color: primary[300] },
                { label: "Novated Lease", comment: "", data: createData(novatedLease, range, YTD), negative: true, hidden: (novatedLease.a <= 0), color: primary[300] },


                { label: "Overtime income", comment: "", data: createData(overtimeSalary, range, YTD), hidden: (overtimeSalary.a === 0), color: primary[300] },
                { label: "Deductions", comment: "", data: createData(taxableDeductions, range, YTD, !adjustDeductions), negative: true, hidden: !adjustDeductions, color: primary[100] },
                { label: (capitalGains > 0 ? "Capital Gain" : "Capital Loss"), comment: "", data: createData(capitalGains, range, YTD), hidden: capitalGains === 0, color: primary[100], squareBrackets: capitalGains < 0, },
                { label: "Allowances", comment: "", data: createData(allowance, range, YTD, !adjustAllowanceIncome), hidden: !adjustAllowanceIncome, squareBrackets: !allowanceIncomeTaxable, color: primary[400] },
                { label: "Other income", comment: "", data: createData(otherIncome, range, YTD, !adjustOtherIncome), hidden: !adjustOtherIncome, color: primary[400] },
                { label: "Bonus pay", comment: "", data: createData(bonusIncomeObj, range, YTD, !hasBonus), hidden: !hasBonus, color: primary[400] },
                { label: "Business income", comment: "", data: createData(businessIncomeDeductable, range, YTD), hidden: businessIncomeDeductable <= 0, color: primary[400] },
                { label: "Business loss", comment: "", data: createData(businessIncomeLoss, range, YTD), negative: true, hidden: businessIncomeLoss <= 0, color: primary[400] },
                // { label: "Superannuation", comment: "Taken from your salary", data: createData(includedSuper, range, YTD), hidden: !includesSuperannuation, roundBrackets: true, color: primary[200] },
                { label: "Fringe Benefits", comment: "", data: createData(fringeBenefitsAnnual, range, YTD), squareBrackets: true, hidden: fringeBenefitsAnnual.a <= 0, color: primary[200] },
                { label: "Franking credits", comment: "", data: createData(frankingCredits, range, YTD), hidden: frankingCredits <= 0, color: primary[200] },

                // { label: "Carry forward Super", comment: "Taxed from Super fund, not income", data: createData(superannuationCarryForward, range, YTD), hidden: (!adjustSuperannuationCarryForward || superannuationCarryForward === 0), negative: true, color: primary[200] },


                { label: "Salary sacrifice super", comment: "", data: createData(superannuationSacrafice, range, YTD, !adjustSalaryScaracfice), negative: true, hidden: !adjustSalaryScaracfice, color: primary[500] },
                { label: "Concessional voluntary Super", comment: "Taxable deduction", data: createData(concessionalAdditionalSuper, range, YTD), hidden: (concessionalAdditionalSuper <= 0), negative: true, color: primary[200] },

                { label: "Non-concessional Super", comment: "Excess concessional contributions", data: createData(superannuationUntaxedNonConcessional, range, YTD), hidden: superannuationUntaxedNonConcessional <= 0, color: primary[700] },
                // { label: "Excess Superannuation", comment: "This exceeds the cap", data: createData(superannuationExcess, range, YTD), squareBrackets: true, hidden: superannuationExcess === 0, color: primary[600] },
            ]
        },
        // SUPER
        {
            label: SUPERANNUATION, shortLabel: "Super", comment: "", id: "expandSuper", data: createData(totalSuperannuation, range, YTD), color: tertiary.main,

            chart: {
                label: "Superannuation", value: formatValue(annualAmount(totalSuperannuation)),
            },


            sub: [
                { label: "Super Guarantee", comment: superGuaranteeComment, data: createData(superGuarantee, range, YTD), color: tertiary[100] },
                { label: "Co-contribution", comment: "Government contribution", data: createData(superannuationCoContribution, range, YTD), hidden: superannuationCoContribution === 0, color: tertiary[400] },
                { label: "Reportable contributions", comment: "Additional employer contributions", data: createData(totalReportableSuper, range, YTD), hidden: totalReportableSuper.a === 0, color: tertiary[400] },
                { label: "Voluntary contributions", comment: "", data: createData(personalContributions, range, YTD), hidden: (!adjustSuperannuation || personalContributions === 0), color: tertiary[300] },

                { label: "Tax Offset", comment: "Low income super tax offset (maximum)", data: createData(listo, range, YTD), negative: true, squareBrackets: true, hidden: (listo === 0), color: tertiary[600] },
                { label: "Tax", comment: superTaxComment, data: createData(superannuationTax, range, YTD), squareBrackets: true, color: nc, showZero: true },

                { label: "Concessional", comment: '', data: createData(superannuationConcessional, range, YTD), color: clear, showZero: true, auxillery: 1 },
                { label: "Non-concessional", comment: '', data: createData(superannationNonConcessional, range, YTD), color: clear, showZero: true, auxillery: 2 },
                { label: "Excess non-concessional", comment: '', data: createData(superannuationExcess, range, YTD), hidden: superannuationExcess === 0, color: clear, auxillery: 2 },

            ]
        },
        // TAX


        {
            label: TAX, shortLabel: "Tax", id: "expandTax", data: createData(grossTax, range, YTD), color: secondary.main,

            chart: {
                label: "Tax", comment: `${taxPercent}% of income`, value: formatValue(annualAmount(grossTax)),
                // extra: [
                //     { label: "Marginal Tax rate", value: `${marginalTaxRate}%` },
                //     { label: "Offsets", value: formatValue(annualAmount(lamito) + annualAmount(lito) + annualAmount(sapto) + annualAmount(spouseSuperTaxOffset)) },
                // ]
            },


            sub: [
                { label: "Income tax", comment: taxCategoryComment, data: createData(incomeTax, range, YTD), color: secondary[300], showZero: true },
                { label: "Extra withholding", comment: "Due to a 53 weekly payment year", data: createData(extraWithholdingTax, range, YTD), color: secondary[300], hidden: extraWithholdingTax.w === 0 },

                { label: studentLoanTitle, comment: "HELP, VET, SSL, TSL, SFSS ", data: createData(help, range, YTD), hidden: !HELP || disableHELP, color: secondary[200] },

                { label: "LMITO", comment: "Low And Middle Income Tax Offset", data: createData(lamito, range, YTD), negative: true, hidden: lamito === 0, color: nc },
                { label: "LITO", comment: "Low Income Tax Offset", data: createData(lito, range, YTD), negative: true, hidden: lito === 0, color: nc },
                { label: "SAPTO", comment: "Seniors And Pensioners Tax Offset", data: createData(sapto, range, YTD), negative: true, hidden: sapto === 0, color: nc },
                { label: "Spouse Superannuation Offset", comment: "", data: createData(spouseSuperTaxOffset, range, YTD), negative: true, hidden: spouseSuperTaxOffset === 0, color: nc },

                { label: "Division 293", comment: "Additional tax on super", data: createData(division293, range, YTD), hidden: division293 === 0, color: (division293 === 0 ? nc : secondary[400]) },
                { label: "Medicare", comment: medicareDescription, data: createData(medicare, range, YTD), color: secondary[500], showZero: true },
                { label: "Medicare Surcharge", comment: "No Private Healthcare", data: createData(medicareSurchargeLiability, range, YTD), hidden: (medicareSurchargeLiability === 0), color: secondary[500] },
                { label: "Excess Superannuation tax", comment: "", data: createData(superannuationExcessTax, range, YTD), hidden: superannuationExcessTax === 0, color: secondary[600] },

                { label: "Medicare adjustment", comment: "Tax witholding reduction", data: createData(medicareAdjustment, range, YTD), negative: true, hidden: !applyMedicareAdjustment || medicareAdjustment.w === 0, color: nc },
                { label: "Other", comment: "Other taxes (see note)", data: createData(otherTaxesAndLevies, range, YTD), hidden: otherTaxesAndLevies === 0, color: (otherTaxesAndLevies === 0 ? nc : secondary[700]) },

                { label: "Tax credits", comment: "", data: createData(taxCredits, range, YTD), negative: true, hidden: taxCredits === 0, color: (taxCredits === 0 ? nc : secondary[700]) },

                { label: "Franking credits", comment: "", data: createData(frankingCredits, range, YTD), negative: true, hidden: frankingCredits === 0, color: (frankingCredits === 0 ? nc : secondary[700]) },

                { label: "GST", comment: "Not included in income tax", data: createData(businessIncomeGST, range, YTD), hidden: businessIncomeGST === 0, squareBrackets: true, color: secondary[700] },
                { label: "ECC Offset", comment: "Excess Super contributions offset", data: createData(superannuationExcessConcessionalContributionsOffset, range, YTD), negative: true, hidden: superannuationExcessConcessionalContributionsOffset === 0, color: secondary[700] },

            ]
        },

    ];
    return summaryData;
}


const annualAmount = (obj) => {
    if (typeof (obj) !== "object") {
        return Math.round(obj);
    }
    return Math.round(obj.a);
}

const formatValue = (val) => {
    return `$${formatMoney(val, 0)}`;
}





// create fields used to represent data wrt the current range and annual range
const createData = (obj, range, nYTD, skip) => {
    let data = { ...obj };
    if (typeof (obj) !== "object") {
        // if the obj is not an object is is presumably a single annual figure
        // convert to a data object
        data = { d: 0, w: 0, f: 0, m: 0, a: Number(obj) };
    }
    if (skip) {
        return { current: 0, complete: 0, annual: 0 }
    }

    const ytd = { d: data.d * nYTD.d, w: data.w * nYTD.w, f: data.f * nYTD.f, m: data.m * nYTD.m, a: data.a * nYTD.a };

    return { current: data[range], currentYTD: ytd[range], complete: data, ytd, annual: data.a }
}

// // create fields used to represent data wrt the current range and annual range
// const createDataAnnualOnly = (obj, range) => {
//     let data = { ...obj };
//     if (typeof (obj) !== "object") {
//         // if the obj is not an object is is presumably a single annual figure
//         // convert to a data object
//         data = { d: NaN, w: NaN, f: NaN, m: NaN, a: Number(obj) };
//     }

//     const ytd = { d: NaN, w: NaN, f: NaN, m: NaN, a: NaN };

//     if(range === "a"){
//         return { current: data.a, currentYTD: ytd.a, complete: data, ytd, annual: data.a }
//     }

//     return { current: NaN, currentYTD:NaN, complete: data, ytd, annual: data.a }
// }

// combine two data objects
const combine = (a, b) => {
    return {
        a: Number(a.a) + Number(b.a),
        m: Number(a.m) + Number(b.m),
        f: Number(a.f) + Number(b.f),
        w: Number(a.w) + Number(b.w),
        d: Number(a.d) + Number(b.d),
    }
}

// if the the a value is zero, combined value is zero
const combineExcludeZero = (a, b) => {
    return {
        a: Number(a.a) === 0 ? 0 : Number(a.a) + Number(b.a),
        m:  Number(a.m) === 0 ? 0 : Number(a.m) + Number(b.m),
        f:  Number(a.f) === 0 ? 0 : Number(a.f) + Number(b.f),
        w:  Number(a.w) === 0 ? 0 : Number(a.w) + Number(b.w),
        d:  Number(a.d) === 0 ? 0 : Number(a.d) + Number(b.d),
    }
}

export default SummaryComponent;